import {cloneElement, Children, PureComponent} from 'react';

class FlowController extends PureComponent {
  // Children elements of this component will get a `stepCompleted` prop, which will be
  // called to advance to the next stage in the flow. It can be called with a
  // string param `flowKey`, whereupon the next stage will be the child element
  // with a corresponding `flowKey` prop. If `true` is passed as the `flowKey`,
  // or if there is no next stage in the flow, the flow is considered to be truly
  // done, and this component's `onFlowFinished` prop will be called.

  constructor(props) {
    super(props);
    this.stepCompleted = this.stepCompleted.bind(this);
    this.getFlowKeyIndex = this.getFlowKeyIndex.bind(this);
    this.next = this.next.bind(this);
    this.done = this.done.bind(this);
    const index = props.startAt ? this.getFlowKeyIndex(props.startAt) || 0 : 0;
    this.startIndex = index;
    this.state = {
      index,
    };
  }

  getFlowKeyIndex(flowKey) {
    const ChildArray = Children.toArray(this.props.children);
    if (typeof flowKey !== 'string') {
      if (Number.isInteger(flowKey)) {
        return flowKey < ChildArray.length ? flowKey : false;
      }
      return false;
    }
    const keyIndex = ChildArray.findIndex((child) => child.props.flowKey === flowKey);
    return keyIndex === -1 ? false : keyIndex;
  }

  next(flowKey, ...args) {
    let done = false;
    const incrementedIndex = Number.isInteger(flowKey) ? flowKey : this.state.index + 1;
    const nextIndex =
      flowKey === false
        ? this.startIndex
        : typeof flowKey === 'string'
        ? this.getFlowKeyIndex(flowKey)
        : incrementedIndex;

    if (this.props.onNext) {
      const childArray = Children.toArray(this.props.children);
      const CurrentItem = childArray[this.state.index];
      const currentFlowKey = CurrentItem && CurrentItem.props ? CurrentItem.props.flowKey : null;
      let nextFlowKey = flowKey;
      if (!flowKey && childArray[nextIndex]) {
        nextFlowKey = childArray[nextIndex].props.flowKey; // if any
      }
      this.props.onNext(
        {index: this.state.index, flowKey: currentFlowKey},
        {index: nextIndex, flowKey: nextFlowKey}
      );
    }

    if (typeof flowKey === 'string') {
      // Child component is jumping to a specific step.
      // Identify the child component with the matching flowKey, and then show it.
      const index = this.getFlowKeyIndex(flowKey);
      if (index !== false) {
        // if the key exists
        this.setState({index});
      }
      return;
    } else if (nextIndex >= Children.count(this.props.children)) {
      // If the current component is the last child then calling "next" means the whole flow is finished.
      done = true;
    }

    if (done) {
      this.done(...args);
    } else {
      this.setState({index: nextIndex});
    }
  }

  done(...args) {
    if (this.props.onFlowFinished) {
      this.props.onFlowFinished(...args);
    }
    if (this.props.stepCompleted) {
      // If this flow is a nested flow then when this flow is done we should tell
      // the *parent* flow to move on.
      // note - this allows both the finished callback and this to be called.
      // NOTE THE ORDER: (child) Done THEN (parent) StepComplete
      this.props.stepCompleted(...args);
    }
    // as we're done, set the state index to -1 so we will return false
    this.setState({index: -1});
    return true;
  }

  stepCompleted(flowKey, ...args) {
    // This method is called when the child component being shown has decided to transition
    // to a new step in the flow.
    if (flowKey === true) {
      // The child component has finished the whole flow.
      this.done(...args);
    } else {
      // otherwise we're all good, lets go.
      this.next(flowKey, ...args);
    }
  }

  render() {
    const CurrentItem = Children.toArray(this.props.children)[this.state.index];
    if (CurrentItem) {
      // Creates a clone of the child that has the stepCompleted callback
      const flowCallbacks = {
        stepCompleted: this.stepCompleted,
      };
      return cloneElement(CurrentItem, flowCallbacks);
    }
    return false;
  }
}

export default FlowController;
